import { Alert, Button, Group, List, Modal, Text } from "@mantine/core";
import { useState } from "react";
import { useDeleteFullMbk } from "../../hooks/useDeleteFullMbk";
import { useSiteNotifications } from "../../hooks/useSiteNotifications";
import { ModalFooter } from "../../mantine/components/ModalFooter/ModalFooter";
import { AlertPresets } from "../../mantine/mantinePresets";
import { useToast } from "../../mantine/notifications/useToast";
import { useShouldShowStaffView } from "../StaffViewToggle/useShouldShowStaffView";
import { DeleteMbksModal } from "./DeleteMbksModal/DeleteMbksModal";
import { EdaVisualization as EdaVisualizationV2 } from "./EdaVisualization/EdaVisualization-v2";
import { useEdaData } from "./EdaVisualization/hooks/useEdaData";

interface EdaVisualizationModalProps {
  siteId: number;
  onCloseModal: () => void;
}

function EdaVisualizationModal({
  siteId,
  onCloseModal
}: EdaVisualizationModalProps) {
  const { showToast, showErrorToast } = useToast();
  const { shouldShowStaffView } = useShouldShowStaffView();
  const edaData = useEdaData(siteId, { isV2: true });
  const {
    mutateAsync: mbkDeleteMutationAsync,
    isPending: isPendingDeleteFullMbk
  } = useDeleteFullMbk(siteId);
  const { data: siteNotifications } = useSiteNotifications(siteId);
  const [deleteConfirmationIsOpen, setDeleteConfirmationIsOpen] =
    useState(false);

  async function handleConfirmDeleteFullMbk(years?: ReadonlyArray<string>) {
    setDeleteConfirmationIsOpen(false);

    try {
      const { status } = await mbkDeleteMutationAsync(years);

      if (status >= 200) {
        showToast({
          type: "success",
          message: "Mess- und Bilanzierungskonzept erfolgreich gelöscht."
        });
        onCloseModal();
      }
    } catch (error) {
      showErrorToast(error);
    }
  }

  const {
    meters_conversion_factor,
    generators_full_feedin,
    tenant_outside_mieterstrom_model
  } = siteNotifications || {};
  const showBanner =
    shouldShowStaffView &&
    generators_full_feedin &&
    tenant_outside_mieterstrom_model &&
    meters_conversion_factor &&
    (generators_full_feedin.length > 0 ||
      meters_conversion_factor.length > 0 ||
      tenant_outside_mieterstrom_model.length > 0);
  const validMessages = showBanner
    ? [
        generators_full_feedin?.length > 0 && (
          <>
            Die folgenden Erzeuger sind als Volleinspeiser markiert:{" "}
            {generators_full_feedin.map((g) => g.name).join(", ")}
          </>
        ),
        meters_conversion_factor?.length > 0 && (
          <>
            Die folgenden Zähler haben einen Wandlungsfaktor:{" "}
            {meters_conversion_factor.map((m) => m.name).join(", ")}
          </>
        ),
        tenant_outside_mieterstrom_model?.length > 0 && (
          <>
            Die folgenden Unternehmen nehmen nicht am Mieterstrommodell Teil:{" "}
            {tenant_outside_mieterstrom_model.map((t) => t.name).join(", ")}
          </>
        )
      ].filter(Boolean)
    : [];
  return (
    <Modal
      className="EdaVisualizationModal"
      opened
      size="90vw"
      title={`Mess- und Bilanzierungskonzept mit zeitlicher Veränderbarkeit`}
      onClose={onCloseModal}
    >
      {showBanner && (
        <Alert
          color={AlertPresets.info.color}
          icon={AlertPresets.info.icon}
          mb="md"
        >
          {validMessages.length > 1 ? (
            <List>
              {validMessages.map((content, index) => (
                <List.Item key={index}>{content}</List.Item>
              ))}
            </List>
          ) : (
            <Text>{validMessages[0]}</Text>
          )}
        </Alert>
      )}
      {edaData.isV2 && (
        <EdaVisualizationV2
          mbksData={edaData.data}
          mbksError={edaData.error}
          mbksIsLoading={edaData.isLoading}
          refetchMbks={edaData.refetchMbks}
          siteId={siteId}
        />
      )}
      <ModalFooter grow>
        <Group justify="space-between">
          <Button
            color="error"
            disabled={!edaData.data || edaData.data.length === 0}
            loading={isPendingDeleteFullMbk}
            onClick={() => setDeleteConfirmationIsOpen(true)}
          >
            Löschen
          </Button>
          <Button variant="default" onClick={onCloseModal}>
            Schließen
          </Button>
        </Group>
      </ModalFooter>
      <DeleteMbksModal
        isOpen={deleteConfirmationIsOpen}
        onClickConfirm={handleConfirmDeleteFullMbk}
        onToggleModal={() => setDeleteConfirmationIsOpen(false)}
      />
    </Modal>
  );
}

export { EdaVisualizationModal };
